import { useEffect } from 'react';
import { useUnit } from 'effector-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { $userStore } from '@lib/store';
import { ValidateUtils } from '@shared/utils';
import { type UserProfileResponse } from '@shared/api';
import { BaseInput, Button, ErrorMessage, Modal, PhoneInput } from '@shared/widgets';
import { Constants } from '@shared/constants';

interface IForm {
  userName: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  position: string;
  phone: { countryCode: string; phoneNumber: string };
}

interface IEditDetailsModalProps {
  show: boolean;
  onClose: () => void;
  user: UserProfileResponse;
}

export const EditDetailsModal = (props: IEditDetailsModalProps) => {
  const userStore = useUnit($userStore.store);

  const { control, formState, handleSubmit, reset } = useForm<IForm>({
    defaultValues: {
      userName: props.user.userName,
      firstName: props.user.firstName,
      lastName: props.user.lastName,
      emailAddress: props.user.emailAddress,
      position: props.user.position,
      phone: { countryCode: props.user.phoneAreaCode || Constants.COUNTRY_CODE, phoneNumber: props.user.phoneNumber },
    },
  });
  const { t } = useTranslation('user');

  useEffect(() => {
    if (!props.show) return;
    reset({
      userName: props.user.userName,
      firstName: props.user.firstName,
      lastName: props.user.lastName,
      emailAddress: props.user.emailAddress,
      position: props.user.position,
      phone: { countryCode: props.user.phoneAreaCode || Constants.COUNTRY_CODE, phoneNumber: props.user.phoneNumber },
    });
  }, [props.show]);

  const closeHandler = () => {
    props.onClose();
    reset({
      userName: props.user.userName,
      firstName: props.user.firstName,
      lastName: props.user.lastName,
      emailAddress: props.user.emailAddress,
      position: props.user.position,
      phone: { countryCode: props.user.phoneAreaCode || Constants.COUNTRY_CODE, phoneNumber: props.user.phoneNumber },
    });
  };
  const submitHandler = async (data: IForm) => {
    const request = {
      ...props.user,
      userName: data.userName,
      firstName: data.firstName,
      lastName: data.lastName,
      emailAddress: data.emailAddress,
      position: data.position,
      phoneAreaCode: data.phone.countryCode,
      phoneNumber: data.phone.phoneNumber,
    };
    await $userStore.events.updateUser({ request, id: props.user.id });
    props.onClose();
  };

  return (
    <Modal isOpen={props.show} onClose={closeHandler} title={t('sidebar.modals.editDetails.title')}>
      <form className='flex flex-col gap-5' noValidate onSubmit={handleSubmit(submitHandler)}>
        <Controller
          name='userName'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} placeholder={t('placeholders.username', { ns: 'common' })} required readOnly />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='firstName'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} placeholder={t('placeholders.firstName', { ns: 'common' })} required />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='lastName'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} placeholder={t('placeholders.lastName', { ns: 'common' })} required />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='emailAddress'
          control={control}
          rules={{
            required: t('errors.form.required', { ns: 'common' }),
            pattern: { value: ValidateUtils.emailRegex, message: t('errors.form.email.invalidFormat', { ns: 'common' }) },
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} type='email' placeholder={t('placeholders.email', { ns: 'common' })} required />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='position'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} placeholder={t('placeholders.positionRole', { ns: 'common' })} required />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='phone'
          control={control}
          rules={{
            required: t('errors.form.required', { ns: 'common' }),
            validate: (value) => {
              if (!value?.countryCode) return t('errors.form.required', { ns: 'common' });
              if (!value?.phoneNumber) return t('errors.form.required', { ns: 'common' });
              if (!ValidateUtils.validatePhoneNumber(value.phoneNumber)) return t('errors.form.phone.invalidFormat', { ns: 'common' });
              return true;
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <PhoneInput enableSearch required value={field.value} onChange={field.onChange} />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Button className='mt-7' fullWidth color='primary' type='submit' disabled={formState.isSubmitting || userStore.loading}>
          {t('buttons.saveChanges', { ns: 'common' })}
        </Button>
      </form>
    </Modal>
  );
};
